/**
 * @description 表格列配置Hook
 * @date 2025-01-17
 * @author lv.chen
 */

import React from 'react';

import SvgIcon from '@home/components/SvgIcon';
import { Bubble } from '@home/components/TeaComponents';
import { formatReleaseTime } from '@home/components/NavHeader/sreachContext/utils';
import { getTrueDataMetricText } from '@intelligence/components/PublicTrueData/utils/utils';
import SortBy from '@intelligence/components/TableComponents/components/SortBy';
import ColumnsCenter from '@intelligence/components/TableComponents/components/CloumsCenter';
import {
  getAlineaDateValue,
  getAlineaPlatformText,
  getAlineaRegionText,
  getAlineaSourceQualityText,
  getAlineaValue,
  formatAlineaValue,
} from '@intelligence/v3/pages/TopCharts/PCConsoleGames/AlineaAnalyticsCalibration/utils';
// import DefaultAvatar from '@/pages/home/assets/img/common/user-default.svg';

import type { TableColumn } from 'tea-component';
import type { IPublicFeedback } from '@intelligence/types/topCharts';
import type { ISortConfig } from '../types';

interface IUseTableColumnsParams {
  mode?: 'di' | 'alinea';
  locale?: string;
  /** 地区/国家树，用于 Alinea 真数 Region 文案转换 */
  regionsCountries?: any[];
  /** 是否为英文 */
  isEN: boolean;
  /** 国际化格式化方法 */
  formatMessage: IFormatMessage;
  /** 是否超过右侧宽度阈值 */
  isOverRightWidth: boolean;
  /** 排序配置 */
  sortConfig: ISortConfig;
  /** 排序变更处理函数 */
  handleSortChange: (event: any) => void;
  /** 来源类型映射 */
  sourceTypeMap: IRecord<string | React.ReactNode>;
  /** 打开图片预览 */
  openImagePreview: (images: string[]) => void;
  /** 打开链接 */
  handleClickLink: (url: string) => void;
  /** 当前用户ID */
  currentUserId?: number;
  /** 打开编辑弹窗 */
  openEdit: (record: IPublicFeedback) => void;
  /** 打开删除确认弹窗 */
  openDeleteConfirm: (id: number) => void;
}

interface IUseTableColumnsReturn {
  /** 表格列配置 */
  columns: TableColumn<IPublicFeedback>[];
}

/** Metrics 列按表格比例分配宽度，兼顾桌面展示与小屏收缩，避免固定像素宽度挤压其他列。 */
const METRIC_COLUMN_WIDTH = '20%';

/**
 * 表格列配置Hook
 */
export default function useTableColumns(params: IUseTableColumnsParams): IUseTableColumnsReturn {
  const {
    // isEN,
    formatMessage,
    mode = 'di',
    locale,
    regionsCountries = [],
    // isOverRightWidth,
    sortConfig,
    handleSortChange,
    sourceTypeMap,
    openImagePreview,
    handleClickLink,
    // currentUserId,
    // openEdit,
    // openDeleteConfirm,
  } = params;

  const { sortKey, sortVal } = sortConfig || {};

  if (mode === 'alinea') {
    const renderText = (value?: string | number): React.ReactNode => <span>{value ?? '-'}</span>;
    const alineaColumns: TableColumn<IPublicFeedback>[] = [
      {
        key: 'metric',
        width: METRIC_COLUMN_WIDTH,
        header: formatMessage({ id: 'dbi_label_metrics' }),
        render: (record) => {
          return record.is_metric_first === false
            ? null
            : renderText(
              getTrueDataMetricText((record as any)?.metric || (record as any)?.metric_name, {
                source: 'alinea',
                locale,
              }),
            );
        },
      },
      {
        key: 'platform',
        header: formatMessage({ id: 'dbi_platform' }),
        render: (record) => renderText(getAlineaPlatformText(record as any)),
      },
      {
        key: 'alinea_calibration_region',
        header: formatMessage({ id: 'dbi_region' }),
        render: (record) => renderText(getAlineaRegionText(record as any, regionsCountries, locale)),
      },
      {
        key: 'release_time',
        header: formatMessage({ id: 'dbi_release' }),
        render: (record) => {
          return renderText(formatReleaseTime(locale, {
            isSpecialReleaseTime: record?.is_special_release_time,
            releaseTime: record?.release_time,
            releaseTimeEN: record?.release_time_en,
            releaseTimeCN: record?.release_time_cn,
          }));
        },
      },
      {
        key: 'date',
        header: <SortBy title={formatMessage({ id: 'dbi_date' })} defaultKey={sortKey} defaultValue={sortVal} onChange={handleSortChange} list={[{ key: 'date', text: formatMessage({ id: 'dbi_date' }), title: formatMessage({ id: 'dbi_date' }), sortMetric: 'date', ratioType: 0 }]} />,
        render: (record) => renderText(getAlineaDateValue(record as any)),
      },
      {
        key: 'value',
        align: 'right',
        header: formatMessage({ id: 'dbi_value' }),
        render: (record) => renderText(formatAlineaValue(getAlineaValue(record as any))),
      },
      {
        key: 'source_quality',
        header: <SortBy title={formatMessage({ id: 'dbi_source_quality' })} defaultKey={sortKey} defaultValue={sortVal} onChange={handleSortChange} list={[{ key: 'source_quality', text: formatMessage({ id: 'dbi_source_quality' }), title: formatMessage({ id: 'dbi_source_quality' }), sortMetric: 'source_quality', ratioType: 0 }]} />,
        render: (record) => renderText(getAlineaSourceQualityText((record as any)?.source_quality, locale)),
      },
      {
        key: 'source',
        header: formatMessage({ id: 'dbi_source' }),
        render: (record) => {
          const source = (record as any)?.source;
          if (!source) return '-';
          return (
            <SvgIcon
              type="other-share-icon"
              className="dbi-combine-details-kpi-trends-show-all-data-modal__source-link"
              onClick={(event) => {
                event?.stopPropagation();
                handleClickLink(source);
              }}
            />
          );
        },
      },
    ];
    return { columns: alineaColumns };
  }

  const columns: TableColumn<IPublicFeedback>[] = [
    {
      key: 'metric_name',
      width: METRIC_COLUMN_WIDTH,
      align: 'left',
      header: (
        <ColumnsCenter align="left" width="100%">
          <SortBy
            title={formatMessage({ id: 'dbi_top_metric' })}
            defaultKey={sortKey}
            defaultValue={sortVal}
            onChange={handleSortChange}
            list={[
              {
                key: 'metric_name',
                text: formatMessage({ id: 'dbi_top_metric' }),
                title: formatMessage({ id: 'dbi_top_metric' }),
                sortMetric: 'metric_name',
                ratioType: 0,
              },
            ]}
          />
        </ColumnsCenter>
      ),
      render: (record) => {
        if (record.is_metric_first === false) {
          return null;
        }

        return (
          <ColumnsCenter align="left" width="100%">
            <span>
              {getTrueDataMetricText(record?.metric || record?.metric_name, {
                source: 'databrain',
                formatMessage,
              })}
            </span>
          </ColumnsCenter>
        );
      },
    },
    {
      key: 'metric_date',
      align: 'center',
      header: (
        <ColumnsCenter align="left" width="100%">
          <SortBy
            title={formatMessage({ id: 'dbi_date' })}
            defaultKey={sortKey}
            defaultValue={sortVal}
            onChange={handleSortChange}
            list={[
              {
                key: 'metric_date',
                text: formatMessage({ id: 'dbi_date' }),
                title: formatMessage({ id: 'dbi_date' }),
                sortMetric: 'metric_date',
                ratioType: 0,
              },
            ]}
          />
        </ColumnsCenter>
      ),
      render: (record) => {
        return (
          <ColumnsCenter align="left" width="100%">
            <span>{record?.metric_date || '-'}</span>
          </ColumnsCenter>
        );
      },
    },
    {
      key: 'metric_value',
      align: 'center',
      header: (
        <ColumnsCenter align="left" width="100%">
          <span>{formatMessage({ id: 'dbi_data' })}</span>
        </ColumnsCenter>
      ),
      render: (record) => {
        const { metric_value: metricValue } = record || {};

        if (!metricValue) {
          return '-';
        }

        return (
          <ColumnsCenter align="left" width="100%">
            <div>
              <Bubble dark arrowPointAtCenter content={metricValue}>
                <div>
                  <span>{metricValue}</span>
                </div>
              </Bubble>
            </div>
          </ColumnsCenter>
        );
      },
    },
    {
      key: 'source_type',
      align: 'center',
      header: (
        <ColumnsCenter align="left" width="100%">
          <SortBy
            title={formatMessage({ id: 'dbi_feedback_source' })}
            defaultKey={sortKey}
            defaultValue={sortVal}
            onChange={handleSortChange}
            list={[
              {
                key: 'source_type',
                text: formatMessage({ id: 'dbi_feedback_source' }),
                title: formatMessage({ id: 'dbi_feedback_source' }),
                sortMetric: 'source_type',
                ratioType: 0,
              },
            ]}
          />
        </ColumnsCenter>
      ),
      render: (record) => {
        const { source_type: sourceType, signature_source_image: signatureSourceImage } = record || {};
        const imageUrls = signatureSourceImage?.split('|')?.filter(Boolean) || [];

        if (!record?.source_type) {
          return '-';
        }

        return (
          <ColumnsCenter align="left" width="100%">
            <div>
              {sourceTypeMap[sourceType]}
              {imageUrls.length > 0 && (
                <SvgIcon
                  type="image-preview"
                  onClick={(e) => {
                    e?.stopPropagation();
                    e?.preventDefault();
                    openImagePreview(imageUrls);
                  }}
                />
              )}
            </div>
          </ColumnsCenter>
        );
      },
    },
    // {
    //   key: 'url',
    //   align: 'left',
    //   width: 120,
    //   header: formatMessage({ id: 'dbi_feedback_link' }),
    //   render: (record) => {
    //     const { source_type: sourceType, url } = record || {};
    //
    //     if (!url) {
    //       return '-';
    //     }
    //
    //     return (
    //       <div>
    //         {sourceType === 'Expert Knowledge' ? (
    //           url
    //         ) : (
    //           <SvgIcon
    //             type="aigc-share"
    //             onClick={(e) => {
    //               e?.stopPropagation();
    //               e?.preventDefault();
    //               handleClickLink(url);
    //             }}
    //           />
    //         )}
    //       </div>
    //     );
    //   },
    // },
    // {
    //   key: 'user_name',
    //   align: 'left',
    //   width: 170,
    //   header: (
    //     <ColumnsCenter align="left" width="100%">
    //       <SortBy
    //         title={formatMessage({ id: 'dbi_feedback_contributor' })}
    //         defaultKey={sortKey}
    //         defaultValue={sortVal}
    //         onChange={handleSortChange}
    //         list={[
    //           {
    //             key: 'user_name',
    //             text: formatMessage({ id: 'dbi_feedback_contributor' }),
    //             title: formatMessage({ id: 'dbi_feedback_contributor' }),
    //             sortMetric: 'user_name',
    //             ratioType: 0,
    //           },
    //         ]}
    //       />
    //     </ColumnsCenter>
    //   ),
    //   render: (record) => {
    //     const { user_name: userName, user_cover: userCover, anonymity } = record || {};
    //
    //     return (
    //       <span style={{ display: 'flex', alignItems: 'center' }}>
    //         <img
    //           style={{ width: 40, height: 40, marginRight: 8, borderRadius: 50, objectFit: 'cover' }}
    //           src={userCover || DefaultAvatar}
    //           onError={(e) => { e.currentTarget.src = DefaultAvatar; }}
    //         />
    //         <span>{anonymity ? formatMessage({ id: 'dbi_EK_user' }) : userName || '-'}</span>
    //       </span>
    //     );
    //   },
    // },
    // {
    //   key: 'fill_time',
    //   align: !isOverRightWidth && isEN ? 'right' : 'left',
    //   width: 190,
    //   header: (
    //     <ColumnsCenter align="left" width="100%">
    //       <SortBy
    //         title={formatMessage({ id: 'dbi_feedback_contributor_time' })}
    //         defaultKey={sortKey}
    //         defaultValue={sortVal}
    //         onChange={handleSortChange}
    //         list={[
    //           {
    //             key: 'fill_time',
    //             text: formatMessage({ id: 'dbi_ddbi_feedback_contributor_timeate' }),
    //             title: formatMessage({ id: 'dbi_feedback_contributor_time' }),
    //             sortMetric: 'fill_time',
    //             ratioType: 0,
    //           },
    //         ]}
    //       />
    //     </ColumnsCenter>
    //   ),
    //   render: (record) => <span>{record?.fill_time || '-'}</span>,
    // },
    // {
    //   key: 'content',
    //   align: 'left',
    //   width: 180,
    //   header: formatMessage({ id: 'dbi_feedback_note' }),
    //   render: (record) => {
    //     const { content } = record || {};
    //
    //     if (!content) {
    //       return '-';
    //     }
    //
    //     return (
    //       <div>
    //         <Bubble dark arrowPointAtCenter content={content}>
    //           <div>
    //             <span>{content}</span>
    //           </div>
    //         </Bubble>
    //       </div>
    //     );
    //   },
    // },
    // {
    //   key: 'options',
    //   align: 'left',
    //   width: 95,
    //   header: formatMessage({ id: 'Action' }),
    //   render: (record) => {
    //     const { id, user_id: userId } = record || {};
    //
    //     return (
    //       <div>
    //         {currentUserId === userId && (
    //           <>
    //             <Bubble dark arrowPointAtCenter content={formatMessage({ id: 'dbi_feedback_edit_public_true_data' })}>
    //               <div>
    //                 <SvgIcon
    //                   type="pencil-m"
    //                   onClick={(e) => {
    //                     e?.stopPropagation();
    //                     e?.preventDefault();
    //                     openEdit(record);
    //                   }}
    //                 />
    //               </div>
    //             </Bubble>
    //             <Bubble dark arrowPointAtCenter content={formatMessage({ id: 'dbi_delete' })}>
    //               <div>
    //                 <SvgIcon
    //                   type="clear-icon"
    //                   onClick={(e) => {
    //                     e?.stopPropagation();
    //                     e?.preventDefault();
    //                     openDeleteConfirm(id);
    //                   }}
    //                 />
    //               </div>
    //             </Bubble>
    //           </>
    //         )}
    //       </div>
    //     );
    //   },
    // },
  ];

  return {
    columns,
  };
}
